import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { LocalizedRef } from "@/lib/api/loads";
import { getTrailerLoadTypes } from "@/lib/api/trailers";
import {
  createTrailerLoadType,
  updateTrailerLoadType,
} from "@/lib/api/catalogs";
import {
  LocalizedNamesFields,
  toLocalizedName,
  type LocalizedNamesDraft,
} from "@/components/admin/catalogs/localized-names";
import { Text } from "@/components/ui/text";
import { CatalogPageTemplate } from "@/components/admin/catalog-page-template";

type TrailerLoadTypeRow = LocalizedRef & {
  created_at?: string;
  is_deleted?: boolean;
};

function AdminTrailerLoadTypesCatalogPage() {
  const { t: tAdminNav } = useTranslation("adminNav");
  const { t: tCatalogLoadTypes } = useTranslation("admin.catalogs.loadTypes");
  const { t: tCommon } = useTranslation("common");

  return (
    <CatalogPageTemplate<TrailerLoadTypeRow>
      title={tAdminNav("trailerLoadTypes")}
      tableId="catalog-trailer-load-types"
      queryKey={["admin", "catalogs", "trailer-load-types"]}
      fetchFn={getTrailerLoadTypes}
      columns={[
        {
          key: "nameEn",
          header: tCatalogLoadTypes("columns.nameEn"),
          defaultWidth: 200,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              weight="medium"
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.en ?? item.name}
            </Text>
          ),
        },
        {
          key: "nameRu",
          header: tCatalogLoadTypes("columns.nameRu"),
          defaultWidth: 180,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.ru ?? "—"}
            </Text>
          ),
        },
        {
          key: "nameUz",
          header: tCatalogLoadTypes("columns.nameUz"),
          defaultWidth: 180,
          minWidth: 100,
          render: (item, isDeleted) => (
            <Text
              className={
                isDeleted
                  ? "text-[color:var(--on-surface-variant)] opacity-70"
                  : ""
              }
            >
              {item.names?.uz ?? "—"}
            </Text>
          ),
        },
      ]}
      searchFilter={(item, q) => {
        const names = [
          item.name,
          item.names?.en,
          item.names?.ru,
          item.names?.uz,
          item.names?.tr,
          item.names?.uz_cyr,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return names.includes(q);
      }}
      searchPlaceholder={`${tCommon("search")}...`}
      emptyMessage={tCommon("noResults")}
      addLabel={tCommon("add")}
      editTitle={`${tCommon("edit")} ${tAdminNav("trailerLoadTypes")}`}
      addTitle={`${tCommon("add")} ${tAdminNav("trailerLoadTypes")}`}
      failedToLoadMessage={tCommon("errorLoadingData")}
      failedToSaveMessage={tCommon("errorOccurred")}
      deleteEndpoint="/trailer-load-types"
      editLabel={tCommon("edit")}
      itemToFormData={(item) => ({
        namesDraft: {
          en: item.names?.en ?? item.name ?? "",
          ru: item.names?.ru ?? "",
          uz: item.names?.uz ?? "",
        } as LocalizedNamesDraft,
      })}
      emptyFormData={() => ({
        namesDraft: { en: "", ru: "", uz: "" } as LocalizedNamesDraft,
      })}
      formDataToPayload={(fd) => ({
        name: (fd.namesDraft as LocalizedNamesDraft).en,
        names: toLocalizedName(fd.namesDraft as LocalizedNamesDraft),
      })}
      isFormValid={(fd) =>
        Boolean((fd.namesDraft as LocalizedNamesDraft).en.trim())
      }
      createFn={(payload) =>
        createTrailerLoadType(
          payload as Parameters<typeof createTrailerLoadType>[0],
        )
      }
      updateFn={(id, payload) =>
        updateTrailerLoadType(
          id,
          payload as Parameters<typeof updateTrailerLoadType>[1],
        )
      }
      renderForm={(formData, setFormData, submitting) => (
        <LocalizedNamesFields
          value={formData.namesDraft as LocalizedNamesDraft}
          onChange={(next) =>
            setFormData((prev) => ({ ...prev, namesDraft: next }))
          }
          disabled={submitting}
          testIdPrefix="admin-trailer-load-type"
        />
      )}
      testIdPrefix="admin-catalog-trailer-load-types"
    />
  );
}

export const Route = createFileRoute("/_app/admin/catalogs/trailer-load-types")(
  {
    component: AdminTrailerLoadTypesCatalogPage,
  },
);
